<template>
    <Tippy
        trigger="click"
        animation="scale"
        :interactive="true"
        :placement="placement"
        z-index="49"
        :append-to="appendTo"
        :popperOptions="mergedPopperOptions"
        :on-show="onShow"
        @click.stop
    >
        <div
            class="inline-flex cursor-pointer rounded-md py-1 transition hover:bg-muval-gray-5"
            :class="$attrs.class"
        >
            <SvgIcon
                name="system-kebab-menu"
                class="h-6 w-6 text-muval-gray-1"
            />
        </div>
        <template #content>
            <slot />
        </template>
    </Tippy>
</template>
<script setup>
import 'tippy.js/animations/scale.css';
import { computed } from 'vue';

const props = defineProps({
    popperOptions: {
        type: Object,
        default: null,
    },
    onShow: {
        type: Function,
        default: () => {},
    },
    placement: {
        type: String,
        default: 'auto-start',
    },
    // Lift the popover out of clipping ancestors (cards, `overflow-hidden`
    // columns) instead of letting tippy append it next to the trigger. Default
    // off keeps tippy's own behaviour so existing usages are unchanged.
    teleport: {
        type: Boolean,
        default: false,
    },
});

// Climb only as far as we need to. <body> escapes every clipping ancestor, but
// it is the wrong target inside a modal: NewMuvalModal is a HeadlessUI Dialog
// (`fixed inset-0 z-50`), so a body-level popover both renders *behind* it
// — our z-index is 49 — and gets marked aria-hidden/inert, because HeadlessUI
// deactivates everything outside the open dialog. Either alone makes the menu
// unusable. Stopping at the dialog escapes the card clipping while staying
// inside the dialog's stacking and focus context.
//
// NOTE: this must resolve `document` in script scope — an inline
// `() => document.body` in the template compiles to `_ctx.document.body`
// (undefined) and throws on mount. tippy invokes `appendTo` with the reference
// element, so we can resolve the target relative to the trigger.
const appendTo = computed(() => {
    if (!props.teleport) {
        return undefined;
    }

    return (reference) => reference.closest('[role="dialog"]') ?? document.body;
});

const defaultPopperOptions = {
    strategy: 'fixed',
    modifiers: [
        {
            name: 'preventOverflow',
            options: {
                altAxis: true,
                tether: false,
            },
        },
    ],
};

const mergedPopperOptions = computed(() => {
    if (!props.popperOptions) {
        return defaultPopperOptions;
    }

    return {
        ...defaultPopperOptions,
        ...props.popperOptions,
        modifiers: [...defaultPopperOptions.modifiers, ...(props.popperOptions.modifiers || [])],
    };
});
</script>
